import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { API, GatewayRoute, GatewayRouteSloTarget, Product } from "../../types";
import { DataSensitivity, RouteAuthMechanism } from "../../types";
import { useGatewaySkus } from "../../hooks/useGatewaySkus";
import { useApis } from "../../hooks/useApis";
import { useProducts } from "../../hooks/useProducts";
import { GATEWAY_ENV_OPTIONS } from "../../utils/gatewaySkuEnv";
import { ROUTE_AUTH_OPTIONS, routeAuthLabel } from "../../utils/gatewayRouteAuth";
import { DATA_SENSITIVITY_OPTIONS, dataSensitivityLabel } from "../../utils/dataSensitivity";
import GatewayRouteSloSection from "./GatewayRouteSloSection";

interface EditGatewayRouteDialogProps {
  route: GatewayRoute | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: GatewayRoute) => void;
}

const EditGatewayRouteDialog: React.FC<EditGatewayRouteDialogProps> = ({
  route,
  open,
  onClose,
  onSaved,
}) => {
  const { gatewaySkus } = useGatewaySkus();
  const { apis } = useApis();
  const { products } = useProducts();
  const [saving, setSaving] = useState(false);
  const [basePath, setBasePath] = useState("");
  const [upstreamServer, setUpstreamServer] = useState("");
  const [upstreamPath, setUpstreamPath] = useState("");
  const [envName, setEnvName] = useState("dev");
  const [gatewayId, setGatewayId] = useState("");
  const [gatewaySkuUuid, setGatewaySkuUuid] = useState("");
  const [gatewayAuth, setGatewayAuth] = useState(RouteAuthMechanism.NONE);
  const [upstreamAuth, setUpstreamAuth] = useState(RouteAuthMechanism.NONE);
  const [gatewayAuthNotes, setGatewayAuthNotes] = useState("");
  const [upstreamAuthNotes, setUpstreamAuthNotes] = useState("");
  const [purpose, setPurpose] = useState("");
  const [dataSensitivity, setDataSensitivity] = useState<DataSensitivity | "">("");
  const [notes, setNotes] = useState("");
  const [sloTargets, setSloTargets] = useState<GatewayRouteSloTarget[]>([]);
  const [selectedApis, setSelectedApis] = useState<API[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!route) return;
    setBasePath(route.basePath);
    setUpstreamServer(route.upstreamServer);
    setUpstreamPath(route.upstreamPath);
    setEnvName(route.envName);
    setGatewayId(route.gatewayId);
    setGatewaySkuUuid(route.gatewaySku?.uuid ?? "");
    setGatewayAuth(route.gatewayAuthMechanism);
    setUpstreamAuth(route.upstreamAuthMechanism);
    setGatewayAuthNotes(route.gatewayAuthNotes ?? "");
    setUpstreamAuthNotes(route.upstreamAuthNotes ?? "");
    setPurpose(route.purpose ?? "");
    setDataSensitivity(route.dataSensitivity ?? "");
    setNotes(route.notes ?? "");
    setSloTargets(route.sloTargets ?? []);
    setSelectedApis(route.apis ?? []);
    setSelectedProducts(route.products ?? []);
  }, [route]);

  const handleSave = async () => {
    if (!route?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateGatewayRoute(route.uuid, {
        basePath: basePath.trim(),
        upstreamServer: upstreamServer.trim(),
        upstreamPath: upstreamPath.trim(),
        envName,
        gatewayId: gatewaySkuUuid ? undefined : gatewayId.trim(),
        gatewaySkuUuid: gatewaySkuUuid || null,
        gatewayAuthMechanism: gatewayAuth,
        upstreamAuthMechanism: upstreamAuth,
        gatewayAuthNotes: gatewayAuthNotes.trim() || null,
        upstreamAuthNotes: upstreamAuthNotes.trim() || null,
        purpose: purpose.trim() || null,
        dataSensitivity: dataSensitivity || null,
        notes: notes.trim() || null,
        sloTargets: sloTargets.filter((s) => s.sloTarget?.trim()),
        apiUuids: selectedApis.map((a) => a.uuid),
        productUuids: selectedProducts.map((p) => p.uuid),
      });
      onSaved(updated);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update route");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit gateway route</DialogTitle>
      <DialogContent>
        <TextField
          label="Gateway base path"
          value={basePath}
          onChange={(e) => setBasePath(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          select
          label="Environment"
          value={envName}
          onChange={(e) => setEnvName(e.target.value)}
          fullWidth
          margin="dense"
        >
          {GATEWAY_ENV_OPTIONS.map((e) => (
            <MenuItem key={e} value={e}>
              {e}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Gateway SKU"
          value={gatewaySkuUuid}
          onChange={(e) => setGatewaySkuUuid(e.target.value)}
          fullWidth
          margin="dense"
        >
          <MenuItem value="">None</MenuItem>
          {gatewaySkus.map((s) => (
            <MenuItem key={s.uuid} value={s.uuid}>
              {s.gatewayCommonName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Gateway ID"
          value={gatewayId}
          onChange={(e) => setGatewayId(e.target.value)}
          disabled={!!gatewaySkuUuid}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Upstream server"
          value={upstreamServer}
          onChange={(e) => setUpstreamServer(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Upstream path"
          value={upstreamPath}
          onChange={(e) => setUpstreamPath(e.target.value)}
          fullWidth
          margin="dense"
        />
        <TextField
          select
          label="Gateway auth"
          value={gatewayAuth}
          onChange={(e) => setGatewayAuth(e.target.value as RouteAuthMechanism)}
          fullWidth
          margin="dense"
        >
          {ROUTE_AUTH_OPTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {routeAuthLabel(a)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Upstream auth"
          value={upstreamAuth}
          onChange={(e) => setUpstreamAuth(e.target.value as RouteAuthMechanism)}
          fullWidth
          margin="dense"
        >
          {ROUTE_AUTH_OPTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {routeAuthLabel(a)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Purpose"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          fullWidth
          margin="dense"
          multiline
        />
        <TextField
          select
          label="Data sensitivity"
          value={dataSensitivity}
          onChange={(e) => setDataSensitivity(e.target.value as DataSensitivity)}
          fullWidth
          margin="dense"
        >
          <MenuItem value="">None</MenuItem>
          {DATA_SENSITIVITY_OPTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {dataSensitivityLabel(d)}
            </MenuItem>
          ))}
        </TextField>
        <GatewayRouteSloSection rows={sloTargets} onChange={setSloTargets} />
        <Autocomplete
          multiple
          options={apis}
          getOptionLabel={(a) => a.name}
          value={selectedApis}
          onChange={(_, v) => setSelectedApis(v)}
          renderInput={(params) => <TextField {...params} label="APIs" margin="dense" />}
          sx={{ mt: 1 }}
        />
        <Autocomplete
          multiple
          options={products}
          getOptionLabel={(p) => p.name}
          value={selectedProducts}
          onChange={(_, v) => setSelectedProducts(v)}
          renderInput={(params) => <TextField {...params} label="Products" margin="dense" />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditGatewayRouteDialog;
